import { useState, useEffect } from 'react';
import { api } from './lib/api';

interface Account {
  numero: string;
  libelle: string;
  classe: number;
  parent_id: string | null;
}

interface GrandLivreLine {
  compte_id: string;
  ecriture_id: string;
  date: string;
  libelle: string;
  numero: number | null;
  statut_validation: string;
  debit_centimes: number;
  credit_centimes: number;
  solde_cumul_centimes: number;
}

export default function PlanDeComptes() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [search, setSearch] = useState('');
  const [classe, setClasse] = useState<number | ''>('');
  const [selectedCompte, setSelectedCompte] = useState<string | null>(null);
  const [grandLivre, setGrandLivre] = useState<GrandLivreLine[]>([]);
  const [entrepriseId, setEntrepriseId] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchAccounts = async () => {
      const data = await api.planComptes.list({
        classe: classe ? classe.toString() : undefined,
        search: search || undefined,
      });
      setAccounts(data);
    };
    const timer = setTimeout(fetchAccounts, 300);
    return () => clearTimeout(timer);
  }, [search, classe]);

  const selectCompte = async (numero: string) => {
    setSelectedCompte(numero);
    if (!entrepriseId) return;
    setLoading(true);
    try {
      const data = await api.planComptes.grandLivre(numero, entrepriseId);
      setGrandLivre(data);
    } catch {
      setGrandLivre([]);
    } finally {
      setLoading(false);
    }
  };

  const hierarchy = accounts.reduce((acc, ac) => {
    const parent = ac.parent_id || '(root)';
    if (!acc[parent]) acc[parent] = [];
    acc[parent].push(ac);
    return acc;
  }, {} as Record<string, Account[]>);

  const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Plan de comptes PCG 2026</h2>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Recherche</label>
            <input
              placeholder="Numéro ou libellé"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Classe</label>
            <select value={classe} onChange={(e) => setClasse(e.target.value ? Number(e.target.value) : '')} className={inputClass}>
              <option value="">Toutes classes</option>
              {[...Array(7)].map((_, i) => (
                <option key={i + 1} value={i + 1}>Classe {i + 1}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Entreprise ID (grand livre)</label>
            <input value={entrepriseId} onChange={(e) => setEntrepriseId(e.target.value)} placeholder="ID entreprise" className={inputClass} />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Comptes ({accounts.length})</h3>
        <div className="space-y-2">
          {Object.entries(hierarchy).map(([parent, children]) => (
            <div key={parent}>
              {parent !== '(root)' && <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mt-3 mb-1 px-2">{parent}</div>}
              {children.map((ac) => (
                <div
                  key={ac.numero}
                  className={`flex items-center gap-4 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                    selectedCompte === ac.numero
                      ? 'bg-blue-50 border border-blue-200'
                      : 'hover:bg-gray-50 border border-transparent'
                  } ${parent !== '(root)' ? 'ml-4' : ''}`}
                  onClick={() => selectCompte(ac.numero)}
                >
                  <span className="font-mono text-sm font-medium text-blue-600 w-16">{ac.numero}</span>
                  <span className="text-sm text-gray-700 flex-1">{ac.libelle}</span>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Classe {ac.classe}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {selectedCompte && entrepriseId && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Grand Livre — Compte {selectedCompte}</h3>
            <button className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors" onClick={() => {
              const headers = ['Date','Libellé','#','Débit (€)','Crédit (€)','Solde cumul (€)'];
              const rows = grandLivre.map(l => [
                new Date(l.date).toISOString().slice(0,10),
                l.libelle,
                l.numero || '',
                (l.debit_centimes/100).toFixed(2),
                (l.credit_centimes/100).toFixed(2),
                (l.solde_cumul_centimes/100).toFixed(2),
              ]);
              const csv = [headers, ...rows].map(r=>r.join(',')).join('\n');
              const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `grand-livre-${selectedCompte}-${entrepriseId}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}>Exporter CSV</button>
          </div>
          {loading ? (
            <p className="text-sm text-gray-500">Chargement...</p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-100">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Libellé</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">#</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Débit</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Crédit</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Solde cumulé</th>
                  </tr>
                </thead>
                <tbody>
                  {grandLivre.map((line, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{new Date(line.date).toLocaleDateString('fr-FR')}</td>
                      <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{line.libelle}</td>
                      <td className="px-4 py-3 text-gray-500 border-b border-gray-100">{line.numero || ''}</td>
                      <td className="px-4 py-3 text-gray-700 border-b border-gray-100 text-right">{(line.debit_centimes / 100).toFixed(2)}</td>
                      <td className="px-4 py-3 text-gray-700 border-b border-gray-100 text-right">{(line.credit_centimes / 100).toFixed(2)}</td>
                      <td className="px-4 py-3 text-gray-700 border-b border-gray-100 text-right font-medium">{(line.solde_cumul_centimes / 100).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
